import React, { useRef, useState } from "react";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import logo from "../../../../assets/logo_transparent.png";

const LocationSection = ({
  shop,
  center,
  setMap,
  travelMode,
  setDirectionsError,
  setRoutePath,
  setTravelMode,
  requestingDirections,
  routePath,
  directionsError,
}) => {
  const toast = useRef(null);
  const [showLocationHelp, setShowLocationHelp] = useState(false);
  const [locationError, setLocationError] = useState("");

  const handleLocationError = (error) => {
    let errorMessage = "Error getting your location";

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage =
          "Location access was denied. Please enable it in your browser settings.";
        setShowLocationHelp(true);
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage =
          "Location information is currently unavailable. Please check your connection and try again.";
        break;
      case error.TIMEOUT:
        errorMessage =
          "The request to get your location timed out. Please try again.";
        break;
      default:
        errorMessage =
          "An unknown error occurred while trying to access your location.";
    }

    setLocationError(errorMessage);
  };

  const handleGetDirections = async () => {
    // Clear previous errors
    setLocationError("");

    // Check if geolocation is available
    if (!navigator.geolocation) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Geolocation is not supported by your browser",
        life: 5000,
      });
      return;
    }

    // Clear any previous toasts
    toast.current.clear();

    // No toast, just proceed with location request

    // Clear any previous errors and paths
    setDirectionsError("");
    setRoutePath([]);

    try {
      // First, try to get the current position
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      // If we get here, we have permission
      toast.current.clear();

      // Proceed with getting directions
      setTravelMode((prev) => {
        if (prev === null) return "DRIVE";
        if (prev === "DRIVE") return "TWO_WHEELER";
        if (prev === "TWO_WHEELER") return "WALK";
        return null;
      });
    } catch (error) {
      // Clear loading toast
      toast.current.clear();
      handleLocationError(error);
    }
  };
  // Helper function to handle retry after permission change
  const handleRetryLocation = async () => {
    // Close the help modal
    setShowLocationHelp(false);

    // Give the browser a moment to update permissions
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Try getting location again
    handleGetDirections();
  };

  // Location help dialog footer with both buttons
  const locationHelpFooter = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "0.5rem",
      }}
    >
      <button
        onClick={() => setShowLocationHelp(false)}
        className="p-button p-button-text"
      >
        Close
      </button>
      <button
        onClick={handleRetryLocation}
        className="p-button p-button-primary"
      >
        I've Enabled Location - Try Again
      </button>
    </div>
  );

  return (
    <div className="location-section">
      <Toast ref={toast} position="top-center" />
      <Dialog
        header="Enable Location Access"
        visible={showLocationHelp}
        style={{ width: "90%", maxWidth: "500px" }}
        onHide={() => setShowLocationHelp(false)}
        footer={locationHelpFooter}
        closable={false}
        modal
      >
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <p>To get directions, you need to enable location access:</p>
            <ol style={{ paddingLeft: "20px", margin: "15px 0" }}>
              <li>
                Look for the location permission prompt in your browser's
                address bar
              </li>
              <li>Click "Allow" or the location icon</li>
              <li>Select "Allow" from the dropdown menu</li>
              <li>
                Click the "I've Enabled Location - Try Again" button below
              </li>
            </ol>
            <p>If you don't see the prompt, you may need to:</p>
            <ul style={{ paddingLeft: "20px", margin: "15px 0" }}>
              <li>Check your browser's URL bar for a location icon</li>
              <li>Look for a popup that might be hidden behind this window</li>
              <li>Refresh the page and try again</li>
            </ul>
          </div>
        </div>
      </Dialog>
      <div className="location-details">
        <h3>Location & Contact</h3>
        {shop.storeAddress && (
          <div className="info-item">
            <strong>Address:</strong>
            <div
              className="info-value multiline-ellipsis"
              title={
                shop.storeAddress.length > 150 ? shop.storeAddress : undefined
              }
            >
              {shop.storeAddress}
            </div>
          </div>
        )}
        {shop.directions && (
          <div className="info-item">
            <strong>Landmark:</strong>
            <div
              className="info-value multiline-ellipsis"
              title={shop.directions.length > 150 ? shop.directions : undefined}
            >
              {shop.directions}
            </div>
          </div>
        )}
        {shop.contactPhone && (
          <div className="info-item">
            <strong>Phone:</strong> {shop.contactPhone}
          </div>
        )}
        {shop.contactEmail && (
          <div className="info-item">
            <strong>Email:</strong> {shop.contactEmail}
          </div>
        )}
        {shop.paymentMethods?.length > 0 && (
          <div className="info-item">
            <strong>Payment Methods:</strong> {shop.paymentMethods.join(", ")}
          </div>
        )}
        {/* Get Directions button at bottom of Location & Contact */}
        {shop.markerPosition && (
          <div style={{ width: "100%", marginTop: 18 }}>
            <button
              type="button"
              className="gmaps-directions-btn location-btn-bottom"
              onClick={handleGetDirections}
              disabled={requestingDirections}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              {/* Icon */}
              {travelMode === null && (
                <i
                  className="pi pi-directions"
                  style={{ color: "#fff", fontSize: 22 }}
                />
              )}
              {travelMode === "DRIVE" && (
                <i
                  className="pi pi-car"
                  style={{ color: "#fff", fontSize: 22 }}
                />
              )}
              {travelMode === "TWO_WHEELER" && (
                // Motorcycle SVG (white)
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ display: "inline", verticalAlign: "middle" }}
                >
                  <path
                    d="M20.13 10.13c-.09-.23-.3-.38-.54-.38h-2.22l-2.1-3.36A.997.997 0 0 0 14 6h-2c-.55 0-1 .45-1 1s.45 1 1 1h1.38l1.17 1.88H7.5c-.28 0-.5.22-.5.5s.22.5.5.5h5.38l1.17 1.88H7.5c-.28 0-.5.22-.5.5s.22.5.5.5h5.38l1.17 1.88H7.5c-.28 0-.5.22-.5.5s.22.5.5.5h7.09c.24 0 .45-.15.54-.38l1.97-4.12c.09-.23.09-.48 0-.71zM5.5 18c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm13 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"
                    fill="#fff"
                  />
                </svg>
              )}
              {travelMode === "WALK" && (
                // Walking SVG (white)
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ display: "inline", verticalAlign: "middle" }}
                >
                  <path
                    d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-1.62 2.96l-2.88 2.88c-.2.2-.2.51 0 .71s.51.2.71 0l2.88-2.88c.2-.2.2-.51 0-.71s-.51-.2-.71 0zm3.12 1.41c.2-.2.2-.51 0-.71s-.51-.2-.71 0l-2.88 2.88c-.2.2-.2.51 0 .71s.51.2.71 0l2.88-2.88zm-5.5 5.5c-.2-.2-.51-.2-.71 0s-.2.51 0 .71l2.88 2.88c.2.2.51.2.71 0s.2-.51 0-.71l-2.88-2.88zm8.5 0l-2.88 2.88c-.2.2-.2.51 0 .71s.51.2.71 0l2.88-2.88c.2-.2.2-.51 0-.71s-.51-.2-.71 0z"
                    fill="#fff"
                  />
                </svg>
              )}
              {/* Label */}
              <span style={{ fontSize: 15, fontWeight: 500 }}>
                {travelMode === null && "Get Directions"}
                {travelMode === "DRIVE" && "Driving"}
                {travelMode === "TWO_WHEELER" && "Motorcycle"}
                {travelMode === "WALK" && "Walking"}
              </span>
              {requestingDirections && (
                <span
                  className="directions-loading-spinner"
                  style={{ marginLeft: 10 }}
                />
              )}
            </button>
          </div>
        )}
      </div>
      <div className="location-map-container">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={center}
          zoom={15}
          onLoad={setMap}
        >
          <Marker
            position={center}
            icon={{
              url: logo,
              scaledSize: new window.google.maps.Size(40, 40),
              origin: new window.google.maps.Point(0, 0),
              anchor: new window.google.maps.Point(20, 40),
            }}
          />
          {routePath.length > 0 && (
            <Polyline
              path={routePath}
              options={{
                strokeColor: "#FF9302",
                strokeWeight: 5,
                icons: [
                  {
                    icon: {
                      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                      scale: 3,
                      strokeColor: "#884610",
                    },
                    offset: "100%",
                    repeat: "100px",
                  },
                ],
              }}
            />
          )}
        </GoogleMap>
        {directionsError && <div>{directionsError}</div>}
      </div>
    </div>
  );
};

export default LocationSection;
